import { Alert, Box, CircularProgress, Stack } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import {
  FeedbackSnackbar,
  FormDialog,
  ResourcePage,
  ResourceCollection,
  StatusBadge,
  type ResourceCollectionState,
  ValueBadge,
} from '../../../design-system';
import {
  CommonCatalogFields,
  commonDraftValid,
  emptyCommonDraft,
  type CommonCatalogDraft,
} from './ReferenceCatalogFields';
import { BackToReferenceData, canEditCatalogItem, catalogActionsColumn, CatalogDetailDialog, useReferenceLifecycle } from './ReferenceLifecycle';
import { ReferenceBulkCreateDialog } from './ReferenceBulkCreateDialog';
import { referenceCreateActions } from './ReferenceCreateActions';
import {
  apiErrorMessage,
  filterCatalog,
  fixtureReferenceCode,
  replaceCatalogItem,
  replaceCatalogItems,
  sortCatalog,
  statusLabel,
  statusOptions,
  statusTone,
  todayInSeoul,
  useOperationKey,
  type ReferenceDataResource,
  type ReferenceSettingsCommonProps,
} from '../model/referenceData';
import { referenceBulkPayloads, referenceBulkWorkflows } from '../model/referenceBulk';
import type {
  ReferenceJobFamily,
  ReferenceJobFamilyChangeInput,
  ReferenceJobFamilyCreateInput,
  ReferenceJobProfile,
} from '../model/types';
type JobFamilyDraft = CommonCatalogDraft;
type JobFamilyEditor = {
  mode: 'create' | 'edit';
  item?: ReferenceJobFamily;
  draft: JobFamilyDraft;
};
export function JobFamilyReferenceSettings({
  port,
  fixtureMode,
  canRead,
  canWrite,
  onBack,
  titleRef,
  familyResource,
  profileResource,
}: ReferenceSettingsCommonProps & {
  familyResource: ReferenceDataResource<ReferenceJobFamily>;
  profileResource: ReferenceDataResource<ReferenceJobProfile>;
}) {
  const [families, setFamilies] = useState<ReferenceJobFamily[]>(familyResource.data ?? []);
  const [selectedFamily, setSelectedFamily] = useState<ReferenceJobFamily>();
  const [editor, setEditor] = useState<JobFamilyEditor>();
  const [familyQuery, setFamilyQuery] = useState('');
  const [familyStatus, setFamilyStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [editorError, setEditorError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [bulkWarning, setBulkWarning] = useState<string>();
  const [bulkOpen, setBulkOpen] = useState(false);
  const operationKey = useOperationKey();
  const bulkWorkflow = useMemo(() => referenceBulkWorkflows.jobFamilies(), []);
  useEffect(() => setFamilies(sortCatalog(familyResource.data ?? [])), [familyResource.data]);
  const filteredFamilies = useMemo(
    () => filterCatalog(families, familyQuery, familyStatus),
    [families, familyQuery, familyStatus],
  );
  const profiles = profileResource.data ?? [];
  const familyColumns = useMemo<GridColDef<ReferenceJobFamily>[]>(
    () => [
      {
        field: 'descriptor',
        renderCell: ({ row }) => <ValueBadge label={row.descriptor} />,
        headerName: '직군',
        flex: 1,
        minWidth: 160,
      },
      {
        field: 'code',
        headerName: '참조 코드',
        width: 170,
      },
      {
        field: 'jobProfileCount',
        headerName: '사용 직무',
        width: 110,
        type: 'number',
        valueGetter: (_value, row) =>
          profileResource.loading || profileResource.error
            ? undefined
            : profiles.filter((profile) => profile.jobFamily?.id === row.id && profile.status !== 'ENDED').length,
        valueFormatter: (value?: number) => (value === undefined ? '-' : `${value}개`),
      },
      {
        field: 'status',
        headerName: '상태',
        width: 140,
        renderCell: ({ row }) => <StatusBadge label={statusLabel(row.status)} tone={statusTone(row.status)} />,
      },
    ],
    [profiles],
  );
  const updateFamily = (item: ReferenceJobFamily) => {
    setFamilies((current) => replaceCatalogItem(current, item));
    setSelectedFamily((current) => (current?.id === item.id ? item : current));
    if (!fixtureMode) {
      familyResource.reload();
      profileResource.reload();
    }
  };
  const familyLifecycle = useReferenceLifecycle<ReferenceJobFamily>({
    kindLabel: '직군',
    fixtureMode,
    change: (id, input, key) => port.changeReferenceJobFamily(id, input as ReferenceJobFamilyChangeInput, key),
    fixtureBlockers: {
      'job-family-engineering': [
        {
          type: 'JOB_PROFILE',
          count: 1,
        },
      ],
    },
    onChanged: updateFamily,
    onNotice: setNotice,
  });
  const openFamilyCreate = () => {
    setEditor({
      mode: 'create',
      draft: emptyCommonDraft(),
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const openFamilyEdit = (item: ReferenceJobFamily) => {
    setSelectedFamily(undefined);
    setEditor({
      mode: 'edit',
      item,
      draft: {
        descriptor: item.descriptor,
        effectiveDate: item.status === 'SCHEDULED' ? item.effectiveFrom : todayInSeoul(),
        reason: '',
      },
    });
    setEditorError(undefined);
    operationKey.reset();
  };
  const changeEditorDraft = <K extends keyof CommonCatalogDraft>(field: K, value: CommonCatalogDraft[K]) => {
    setEditor((current) =>
      current
        ? {
            ...current,
            draft: {
              ...current.draft,
              [field]: value,
            },
          }
        : current,
    );
    setEditorError(undefined);
  };
  const save = () => {
    if (!editor || !commonDraftValid(editor.draft, editor.mode) || busy) return;
    setBusy(true);
    setEditorError(undefined);
    let request: Promise<ReferenceJobFamily>;
    if (editor.mode === 'create') {
      const payload: ReferenceJobFamilyCreateInput = {
        descriptor: editor.draft.descriptor.trim(),
        effectiveDate: editor.draft.effectiveDate,
        reason: editor.draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            id: `job-family-fixture-${crypto.randomUUID()}`,
            code: fixtureReferenceCode(),
            descriptor: payload.descriptor,
            status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' : 'ACTIVE',
            effectiveFrom: payload.effectiveDate,
            version: 1,
          })
        : port.createReferenceJobFamily(payload, key);
    } else {
      const payload: ReferenceJobFamilyChangeInput = {
        version: editor.item!.version,
        descriptor: editor.draft.descriptor.trim(),
        effectiveDate: editor.draft.effectiveDate,
        reason: editor.draft.reason.trim(),
      };
      const key = operationKey.forPayload(payload);
      request = fixtureMode
        ? Promise.resolve({
            ...editor.item!,
            descriptor: payload.descriptor ?? editor.item!.descriptor,
            effectiveFrom: payload.effectiveDate,
            version: editor.item!.version + 1,
          })
        : port.changeReferenceJobFamily(editor.item!.id, payload, key);
    }
    const savedMode = editor.mode;
    void request
      .then(
        (saved) => {
          updateFamily(saved);
          setEditor(undefined);
          setNotice(`${saved.descriptor} 직군을 ${savedMode === 'create' ? '등록' : '변경'}했습니다.`);
          operationKey.reset();
        },
        (cause: unknown) => setEditorError(apiErrorMessage(cause, '직군 정보를 저장하지 못했습니다.')),
      )
      .finally(() => setBusy(false));
  };
  const familyTableState: ResourceCollectionState = familyResource.loading
    ? {
        kind: 'loading',
        label: '직군 목록을 불러오는 중입니다.',
      }
    : familyResource.error
      ? {
          kind: 'error',
          message: familyResource.error,
          onRetry: familyResource.reload,
          retryLabel: '직군 목록 다시 시도',
        }
      : families.length === 0
        ? {
            kind: 'empty',
            title: '등록된 직군이 없습니다.',
            description: canWrite ? '직군 등록을 눌러 첫 직군을 추가해 주세요.' : '등록된 직군이 없습니다.',
          }
        : {
            kind: 'ready',
          };
  return (
    <>
      <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice(undefined)} />
      <ResourcePage
        title="직군"
        titleRef={titleRef}
        width="standard"
        navigation={<BackToReferenceData onBack={onBack} />}
        {...(canWrite ? referenceCreateActions({
          createLabel: "직군 등록",
          onCreate: openFamilyCreate,
          onBulkCreate: () => setBulkOpen(true),
        }) : {})}
      >
        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {bulkWarning && <Alert severity="warning" onClose={() => setBulkWarning(undefined)} sx={{ mb: 1.5, flexShrink: 0 }}>{bulkWarning}</Alert>}
          {!canRead ? (
            <Stack
              spacing={1.5}
              sx={{
                mb: canRead ? 1.5 : 0,
                flexShrink: 0,
              }}
            >
              {!canRead ? (
                <Alert severity="warning">직군 기준정보를 조회하려면 staffing.read 권한이 필요합니다.</Alert>
              ) : null}
            </Stack>
          ) : null}
          {canRead ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
              }}
            >
              <ResourceCollection
                state={familyTableState}
                rows={filteredFamilies}
                totalCount={families.length}
                columns={[...familyColumns, catalogActionsColumn({ canWrite, onInspect: setSelectedFamily, onEdit: openFamilyEdit, onLifecycle: familyLifecycle.open })]}
                query={familyQuery}
                onQueryChange={setFamilyQuery}
                searchPlaceholder="직군 이름 또는 코드 검색"
                rowDisplayMode="all"
                showTotalCount={false}
                filters={[
                  {
                    label: '상태',
                    value: familyStatus,
                    onChange: setFamilyStatus,
                    options: statusOptions,
                  },
                ]}
                onRowClick={({ row }) => canWrite && canEditCatalogItem(row) ? openFamilyEdit(row) : setSelectedFamily(row)}
              />
            </Box>
          ) : null}
        </Box>
      </ResourcePage>

      <CatalogDetailDialog
        item={selectedFamily}
        kindLabel="직군"
        canWrite={canWrite}
        canEdit={selectedFamily?.status !== 'SCHEDULED'}
        editDisabledReason={
          selectedFamily?.status === 'SCHEDULED' ? '사용 예정인 직군 속성은 적용일 이후 수정할 수 있습니다.' : undefined
        }
        extraItems={
          selectedFamily
            ? [
                [
                  '연결된 직무',
                  profileResource.loading
                    ? <CircularProgress size={16} aria-label="연결된 직무를 불러오는 중입니다." />
                    : profileResource.error
                      ? '확인할 수 없음'
                      : `${profiles.filter((profile) => profile.jobFamily?.id === selectedFamily.id && profile.status !== 'ENDED').length}개`,
                ],
              ]
            : []
        }
        onClose={() => setSelectedFamily(undefined)}
        onEdit={() => selectedFamily && openFamilyEdit(selectedFamily)}
        onLifecycle={(action) => selectedFamily && familyLifecycle.open(action, selectedFamily)}
      />
      <FormDialog
        open={Boolean(editor)}
        onClose={() => {
          if (!busy) setEditor(undefined);
        }}
        onSubmit={save}
        title={editor ? `직군 ${editor.mode === 'create' ? '등록' : '수정'}` : ''}
        cancelLabel="취소"
        submitLabel="변경사항 저장"
        busy={busy}
        busyLabel="저장 중…"
        submitDisabled={!editor || !commonDraftValid(editor.draft, editor.mode)}
        maxWidth="sm"
      >
        {editor ? (
          <Stack spacing={2}>
            {editorError ? (
              <Alert severity="error" role="alert">
                {editorError}
              </Alert>
            ) : null}
            <CommonCatalogFields
              draft={editor.draft}
              mode={editor.mode}
              busy={busy}
              descriptorHelper="직무를 묶는 상위 분류 이름입니다."
              onChange={changeEditorDraft}
            />
          </Stack>
        ) : null}
      </FormDialog>
      <ReferenceBulkCreateDialog<ReferenceJobFamily>
        open={bulkOpen}
        workflow={bulkWorkflow}
        onClose={() => setBulkOpen(false)}
        create={(row, key) => {
          const payload = referenceBulkPayloads.jobFamily(row);
          return fixtureMode
            ? Promise.resolve({
                id: `job-family-fixture-${crypto.randomUUID()}`,
                code: fixtureReferenceCode(),
                descriptor: payload.descriptor,
                status: payload.effectiveDate > todayInSeoul() ? 'SCHEDULED' as const : 'ACTIVE' as const,
                effectiveFrom: payload.effectiveDate,
                version: 1,
              })
            : port.createReferenceJobFamily(payload, key);
        }}
        onCreated={(created) => {
          setFamilies((current) => replaceCatalogItems(current, created));
          if (!fixtureMode) {
            familyResource.reload();
            profileResource.reload();
          }
        }}
        onApplied={({ succeeded, failed }) => {
          setBulkWarning(failed ? `직군 ${succeeded.toLocaleString()}개를 등록했고 ${failed.toLocaleString()}개는 실패했습니다.` : undefined);
          setNotice(failed ? undefined : `직군 ${succeeded.toLocaleString()}개를 등록했습니다.`);
        }}
      />
      {familyLifecycle.dialog}
    </>
  );
}
